import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';
import {
  getStudentTokenLogs,
  type StudentWallet,
  type TokenUsageLog,
  formatMoney,
  formatSignedMoney,
  formatNetMoney
} from '../../../lib/apis_vibe';

interface LogViewerProps {
  student: StudentWallet | null;
  isOpen: boolean;
  onClose: () => void;
}

const LogViewer: React.FC<LogViewerProps> = ({ student, isOpen, onClose }) => {
  const [logs, setLogs] = useState<TokenUsageLog[]>([]);
  const [loading, setLoading] = useState(false);
  
  // 筛选状态
  const [filterType, setFilterType] = useState<'all' | 'recharge' | 'consume'>('all');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  useEffect(() => {
    if (isOpen && student) {
      fetchLogs();
    }
  }, [isOpen, student]);

  const fetchLogs = async () => {
    if (!student) return;
    setLoading(true);
    try {
      // 获取最近 200 条记录供前端分析（如需更多历史需后端支持分页加载更多）
      const res = await getStudentTokenLogs(student.user_id, 0, 200);
      // 兼容可能的 AxiosResponse 或直接数据返回
      setLogs((res as any).data || res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 前端过滤逻辑
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // 1. 类型筛选
      if (filterType === 'recharge' && log.amount > 0) return false; // amount < 0 是充值
      if (filterType === 'consume' && log.amount < 0) return false;  // amount > 0 是消耗

      // 2. 时间筛选
      const logDate = new Date(log.created_at).getTime();
      if (dateStart && logDate < new Date(dateStart).getTime()) return false;
      if (dateEnd) {
        const endDate = new Date(dateEnd);
        endDate.setHours(23, 59, 59, 999);
        if (logDate > endDate.getTime()) return false;
      }
      return true;
    });
  }, [logs, filterType, dateStart, dateEnd]);

  // 实时小计
  const stats = useMemo(() => {
    let rechargeTotal = 0;
    let consumeTotal = 0;
    let rechargeMoney = 0;
    let consumeMoney = 0;
    filteredLogs.forEach(log => {
      const tm = Number(log.total_money) || 0;
      if (log.amount < 0) {
        rechargeTotal += Math.abs(log.amount);
        rechargeMoney += Math.abs(tm);
      } else {
        consumeTotal += log.amount;
        consumeMoney += tm;
      }
    });
    return {
      rechargeTotal, consumeTotal, net: rechargeTotal - consumeTotal,
      rechargeMoney, consumeMoney, netMoney: rechargeMoney - consumeMoney
    };
  }, [filteredLogs]);

  if (!isOpen || !student) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end z-50">
      <div className="bg-white h-full w-full max-w-2xl shadow-2xl p-6 overflow-hidden flex flex-col animate-in slide-in-from-right duration-300">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6 border-b pb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{student.username} 的资金明细</h2>
            <p className="text-sm text-gray-500 mt-1">
              当前余额: <span className="font-bold text-blue-600">{student.balance.toLocaleString()}</span> | 
              累计消耗: <span className="font-bold text-gray-700">{student.total_used.toLocaleString()}</span>
              <span className="text-xs text-gray-400 ml-1">{formatMoney(student.used_money)}</span>
            </p>
            <div className="flex gap-2 mt-1">
              <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{student.role || '学员'}</span>
              {student.memo && <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{student.memo}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-3 gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="text-xs text-gray-500 block mb-1">交易类型</label>
            <select 
              value={filterType} 
              onChange={e => setFilterType(e.target.value as any)}
              className="w-full text-sm border-gray-300 rounded-md p-1.5 focus:ring-2 focus:ring-brand-500 outline-none"
            >
              <option value="all">全部类型</option>
              <option value="recharge">仅看充值 (收入)</option>
              <option value="consume">仅看消耗 (支出)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">开始日期</label>
            <input 
              type="date" 
              value={dateStart} 
              onChange={e => setDateStart(e.target.value)}
              className="w-full text-sm border-gray-300 rounded-md p-1.5 focus:ring-2 focus:ring-brand-500 outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">结束日期</label>
            <input 
              type="date" 
              value={dateEnd} 
              onChange={e => setDateEnd(e.target.value)}
              className="w-full text-sm border-gray-300 rounded-md p-1.5 focus:ring-2 focus:ring-brand-500 outline-none"
            />
          </div>
        </div>

        {/* Stats Panel */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1 bg-green-50 p-3 rounded-lg border border-green-100">
            <div className="text-xs text-green-600 flex items-center gap-1"><TrendingUp size={12}/> 区间充值</div>
            <div className="text-lg font-bold text-green-700">+{stats.rechargeTotal.toLocaleString()}</div>
            <div className="text-xs text-green-500 mt-0.5">+{formatMoney(stats.rechargeMoney)}</div>
          </div>
          <div className="flex-1 bg-red-50 p-3 rounded-lg border border-red-100">
            <div className="text-xs text-red-600 flex items-center gap-1"><TrendingDown size={12}/> 区间消耗</div>
            <div className="text-lg font-bold text-red-700">-{stats.consumeTotal.toLocaleString()}</div>
            <div className="text-xs text-red-500 mt-0.5">-{formatMoney(stats.consumeMoney)}</div>
          </div>
          <div className="flex-1 bg-gray-50 p-3 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-600">区间净增</div>
            <div className={`text-lg font-bold ${stats.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.net > 0 ? '+' : ''}{stats.net.toLocaleString()}
            </div>
            <div className={`text-xs mt-0.5 ${stats.netMoney >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {formatNetMoney(stats.netMoney)}
            </div>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-10 text-gray-500">加载流水中...</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-10 text-gray-400">当前筛选条件下无记录</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">时间</th>
                  <th className="px-3 py-2 text-left">类型</th>
                  <th className="px-3 py-2 text-right">变动数量</th>
                  <th className="px-3 py-2 text-left pl-6">备注</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredLogs.map(log => {
                  const isRecharge = log.amount < 0;
                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          log.related_type === 'chat_message' ? 'bg-purple-100 text-purple-700' :
                          log.related_type === 'text' ? 'bg-green-100 text-green-700' :
                          log.related_type === 'image' ? 'bg-blue-100 text-blue-700' :
                          log.related_type === 'video' ? 'bg-indigo-100 text-indigo-700' :
                          log.related_type === 'other' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {log.related_type === 'chat_message' ? 'AI对话' :
                           log.related_type === 'text' ? '文本生成' :
                           log.related_type === 'image' ? '图片生成' :
                           log.related_type === 'video' ? '视频生成' :
                           log.related_type === 'other' && log.amount < 0 ? '充值' :
                           log.related_type === 'other' ? '其他操作' :
                           '后台操作'}
                        </span>
                      </td>
                      <td className={`px-3 py-3 text-right font-medium ${isRecharge ? 'text-green-600' : 'text-red-600'}`}>
                        <div>{isRecharge ? `+${Math.abs(log.amount)}` : `-${log.amount}`}</div>
                        <div className="text-xs text-gray-400 font-normal mt-0.5">
                          {formatSignedMoney(log.total_money, isRecharge)}
                        </div>
                      </td>
                      <td className="px-3 py-3 pl-6 text-gray-600 truncate max-w-[200px]">
                        {log.remark || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogViewer;
